import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import widgetRegistry from './widgetRegistry';
import VARIANT_LISTS, { getVariantMeta } from './variantFamilies';
import WidgetMiniPreview, { useInView } from './WidgetMiniPreview';
import {
    CUSTOM_STYLE_TILE, CUSTOMIZABLE_CATEGORIES,
    AppearanceFields, GaugeShapeFields, TableShapeFields, MetricShapeFields, MetricFieldsEditor,
    computeCardStyle, computeBodyStyle,
} from './WidgetStyleEditor';

const CATEGORIES = [
    { key: 'chart', label: 'Chart', icon: 'fas fa-chart-line', family: 'chart', baseType: 'trend-chart' },
    { key: 'gauge', label: 'Gauge', icon: 'fas fa-tachometer-alt', family: 'gauge', baseType: 'voltage-gauge' },
    { key: 'metric', label: 'Metric', icon: 'fas fa-hashtag', family: 'metric', baseType: 'metric' },
    { key: 'table', label: 'Table', icon: 'fas fa-table', family: 'table', baseType: 'table' },
];

const DEFAULT_CUSTOM_SETTINGS = {
    color: '#4318FF',
    background: '#ffffff',
    shadow: 'sm',
    borderStyle: 'none',
    borderWidth: 2,
    borderRadius: 10,
    fontSize: 28,
    fontWeight: '700',

    customTrackShape: 'circle',
    customFillStyle: 'solid',
    customValueDisplay: 'number',
    customOrientation: 'horizontal',
    customThickness: 14,

    customShowIcon: false,
    customIconClass: '',
    customShowTrend: false,
    customFields: [{ title: 'Today', unit: 'kWh', position: 'center' }],

    customStriped: false,
    customBordered: false,
    customCompact: false,
    customDarkHeader: false,
    customHover: false,
};

const parseNumbers = (str) => (str || '')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => !Number.isNaN(n));

const parseList = (str) => (str || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

let rowSeq = 0;
const newSeriesRow = (name = '', type = 'column', values = '') => ({ key: `row${++rowSeq}`, name, type, values });
const newPairRow = (label = '', value = '') => ({ key: `row${++rowSeq}`, label, value });
const newTableRow = (cells = '') => ({ key: `row${++rowSeq}`, cells });

const StyleTile = ({ style, selected, onSelect, previewConfig }) => {
    const [previewRef, inView] = useInView('300px 0px', '.custom-widget-builder__body');
    return (
        <div
            role="button"
            tabIndex={0}
            className={`widget-tile widget-settings-drawer__style-tile${selected ? ' widget-tile--selected' : ''}`}
            title={style.label}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
        >
            <div className="widget-tile__preview" ref={previewRef}>
                {inView && <WidgetMiniPreview config={previewConfig} />}
            </div>
            <div className="widget-tile__label">{style.label}</div>
        </div>
    );
};

const CustomWidgetBuilder = ({ open, onClose, onCreate }) => {
    const [category, setCategory] = useState('chart');
    const activeCategory = CATEGORIES.find((c) => c.key === category);
    const meta = widgetRegistry[activeCategory.baseType];
    const PreviewWidgetComponent = meta.component;
    const styleOptions = VARIANT_LISTS[activeCategory.family] || [];

    const [title, setTitle] = useState('');
    const [variant, setVariant] = useState(styleOptions[0]?.id);
    const [widthUnits, setWidthUnits] = useState(meta.defaultLayout.w);
    const [heightUnits, setHeightUnits] = useState(meta.defaultLayout.h);
    const [customSettings, setCustomSettings] = useState(DEFAULT_CUSTOM_SETTINGS);
    const patchCustomSettings = (patch) => setCustomSettings((prev) => ({ ...prev, ...patch }));

    const [categoriesText, setCategoriesText] = useState('Mon,Tue,Wed,Thu,Fri,Sat,Sun');
    const [seriesRows, setSeriesRows] = useState([newSeriesRow('Actual', 'column', '120,132,101,134,90,60,70')]);
    const [pairRows, setPairRows] = useState([newPairRow('Lighting', '44'), newPairRow('HVAC', '55')]);

    const [gaugeMax, setGaugeMax] = useState(100);
    const [gaugeUnit, setGaugeUnit] = useState('');

    const [metricUnit, setMetricUnit] = useState('kWh');
    const [metricPrecision, setMetricPrecision] = useState(1);

    const [tableColumns, setTableColumns] = useState('Meter,Status,kWh');
    const [tableRows, setTableRows] = useState([newTableRow('GT1_EMS1,Active,128.4'), newTableRow('GT2_EMS2,Active,96.1')]);

    useEffect(() => {
        const opts = VARIANT_LISTS[activeCategory.family] || [];
        setVariant(opts[0]?.id);
        setWidthUnits(meta.defaultLayout.w);
        setHeightUnits(meta.defaultLayout.h);
    }, [category]);

    if (!open) return null;

    const variantMeta = getVariantMeta(activeCategory.family, variant);
    const chartFamilySub = category === 'chart' ? (variantMeta?.family || 'xy') : null;
    const isCombo = category === 'chart' && variant === 'combo';
    const styleOptionsDisplay = CUSTOMIZABLE_CATEGORIES.has(category) ? [...styleOptions, CUSTOM_STYLE_TILE] : styleOptions;
    const isCustomStyle = variant === 'custom';

    const updateSeriesRow = (key, patch) => setSeriesRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    const addSeriesRow = () => setSeriesRows((prev) => [...prev, newSeriesRow(`Series ${prev.length + 1}`, 'line', '')]);
    const removeSeriesRow = (key) => setSeriesRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

    const updatePairRow = (key, patch) => setPairRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    const addPairRow = () => setPairRows((prev) => [...prev, newPairRow('', '')]);
    const removePairRow = (key) => setPairRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

    const updateTableRow = (key, cells) => setTableRows((prev) => prev.map((r) => (r.key === key ? { ...r, cells } : r)));
    const addTableRow = () => setTableRows((prev) => [...prev, newTableRow('')]);
    const removeTableRow = (key) => setTableRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

    function appearanceSettings() {
        return {
            color: customSettings.color,
            background: customSettings.background,
            shadow: customSettings.shadow,
            borderStyle: customSettings.borderStyle,
            borderWidth: Number(customSettings.borderWidth) || 0,
            borderRadius: Number(customSettings.borderRadius) || 0,
            fontSize: Number(customSettings.fontSize) || undefined,
            fontWeight: customSettings.fontWeight,
        };
    }

    function customShapeSettings(styleId) {
        if (styleId !== 'custom') return {};
        if (category === 'gauge') {
            return {
                customTrackShape: customSettings.customTrackShape,
                customFillStyle: customSettings.customFillStyle,
                customValueDisplay: customSettings.customValueDisplay,
                customOrientation: customSettings.customOrientation,
                customThickness: Number(customSettings.customThickness) || 14,
            };
        }
        if (category === 'metric') {
            return {
                customShowIcon: customSettings.customShowIcon,
                customIconClass: (customSettings.customIconClass || '').trim(),
                customShowTrend: customSettings.customShowTrend,
            };
        }
        if (category === 'table') {
            return {
                customStriped: customSettings.customStriped,
                customBordered: customSettings.customBordered,
                customCompact: customSettings.customCompact,
                customDarkHeader: customSettings.customDarkHeader,
                customHover: customSettings.customHover,
            };
        }
        return {};
    }

    function buildSettingsAndDataSource(styleId) {
        if (category === 'chart') {
            const settings = appearanceSettings();
            const sub = getVariantMeta('chart', styleId)?.family || 'xy';
            if (sub === 'xy') {
                const cats = parseList(categoriesText);
                const series = seriesRows
                    .filter((r) => r.name.trim())
                    .map((r) => {
                        const entry = { name: r.name.trim(), data: parseNumbers(r.values) };
                        if (styleId === 'combo') entry.type = r.type;
                        return entry;
                    });
                return { settings, dataSource: series.length ? { categories: cats, series } : {} };
            }
            if (sub === 'labeled') {
                const rows = pairRows.filter((r) => r.label.trim());
                return {
                    settings,
                    dataSource: rows.length
                        ? { labels: rows.map((r) => r.label.trim()), series: rows.map((r) => Number(r.value) || 0) }
                        : {},
                };
            }
            return { settings, dataSource: {} };
        }
        if (category === 'gauge') {
            return {
                settings: {
                    ...appearanceSettings(),
                    ...customShapeSettings(styleId),
                    value: 0,
                    max: Number(gaugeMax) || 100,
                    unit: gaugeUnit,
                },
                dataSource: {},
            };
        }
        if (category === 'metric') {
            if (styleId === 'custom') {
                const fields = (customSettings.customFields || [])
                    .filter((f) => (f.title || '').trim())
                    .map((f) => ({
                        title: (f.title || '').trim(),
                        unit: (f.unit || '').trim(),
                        position: f.position,
                    }));
                return {
                    settings: {
                        ...appearanceSettings(),
                        ...customShapeSettings(styleId),
                        customFields: fields,
                        value: 0,
                        precision: Number(metricPrecision) || 0,
                    },
                    dataSource: {},
                };
            }
            return {
                settings: {
                    ...appearanceSettings(),
                    value: 0,
                    unit: metricUnit,
                    precision: Number(metricPrecision) || 0,
                },
                dataSource: {},
            };
        }
        const columns = parseList(tableColumns);
        const rows = tableRows.map((r) => parseList(r.cells)).filter((r) => r.length);
        return {
            settings: { ...appearanceSettings(), ...customShapeSettings(styleId) },
            dataSource: rows.length ? { columns, rows } : {},
        };
    }

    const previewConfigFor = (styleId) => {
        const { settings, dataSource } = buildSettingsAndDataSource(styleId);
        return {
            id: `preview-builder-${activeCategory.key}-${styleId}`,
            type: activeCategory.baseType,
            title: title || 'Preview',
            variant: styleId,
            layout: { x: 0, y: 0, w: 4, h: 4, minW: 1, minH: 1 },
            settings: { ...meta.defaultSettings, ...settings },
            dataSource: { ...meta.defaultDataSource, ...dataSource },
        };
    };

    const handleCreate = () => {
        const { settings, dataSource } = buildSettingsAndDataSource(variant);
        onCreate({
            type: activeCategory.baseType,
            variant,
            title: title.trim() || `${activeCategory.label} Widget`,
            settings,
            dataSource,
            layout: { w: Number(widthUnits) || meta.defaultLayout.w, h: Number(heightUnits) || meta.defaultLayout.h },
        });
        onClose();
    };

    const livePreviewSettings = { ...appearanceSettings(), ...customShapeSettings(variant) };

    return createPortal(
        <aside className="custom-widget-builder">
            <div className="custom-widget-builder__header">
                <i className="fas fa-wand-magic-sparkles"></i>
                <span>Create Your Own Widget</span>
                <button type="button" className="widget-drawer__close" title="Close" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>
            </div>

            <div className="custom-widget-builder__body">
                <div className="widget-settings-drawer__field">
                    <span>Widget type</span>
                    <div className="custom-widget-builder__category-row">
                        {CATEGORIES.map((c) => (
                            <button
                                type="button"
                                key={c.key}
                                className={`custom-widget-builder__category${category === c.key ? ' custom-widget-builder__category--active' : ''}`}
                                onClick={() => setCategory(c.key)}
                            >
                                <i className={c.icon}></i> {c.label}
                            </button>
                        ))}
                    </div>
                </div>

                <label className="widget-settings-drawer__field">
                    <span>Title</span>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={`My ${activeCategory.label} Widget`}
                    />
                </label>

                {styleOptionsDisplay.length > 0 && (
                    <div className="widget-settings-drawer__field">
                        <span>Style ({styleOptionsDisplay.length})</span>
                        <div className="widget-settings-drawer__style-grid">
                            {styleOptionsDisplay.map((style) => (
                                <StyleTile
                                    key={style.id}
                                    style={style}
                                    selected={variant === style.id}
                                    onSelect={() => setVariant(style.id)}
                                    previewConfig={previewConfigFor(style.id)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {isCustomStyle && category === 'gauge' && (
                    <GaugeShapeFields settings={customSettings} onChange={patchCustomSettings} />
                )}
                {isCustomStyle && category === 'metric' && (
                    <MetricShapeFields settings={customSettings} onChange={patchCustomSettings} />
                )}
                {isCustomStyle && category === 'table' && (
                    <TableShapeFields settings={customSettings} onChange={patchCustomSettings} />
                )}

                <AppearanceFields settings={customSettings} onChange={patchCustomSettings} />

                <div className="custom-widget-builder__preview-card">
                    <div className="widget-card" style={computeCardStyle(livePreviewSettings)}>
                        <div className="widget-card__header widget-drag-handle">
                            {meta.icon && <i className={`${meta.icon} widget-card__icon`}></i>}
                            <span className="widget-card__title">{title || `My ${activeCategory.label} Widget`}</span>
                        </div>
                        <div className="widget-card__body" style={computeBodyStyle(livePreviewSettings)}>
                            <PreviewWidgetComponent config={previewConfigFor(variant)} />
                        </div>
                    </div>
                </div>

                <div className="widget-settings-drawer__row">
                    <label className="widget-settings-drawer__field">
                        <span>Width (cols)</span>
                        <input type="number" min={1} max={12} step="0.1" value={widthUnits} onChange={(e) => setWidthUnits(e.target.value)} />
                    </label>
                    <label className="widget-settings-drawer__field">
                        <span>Height (rows)</span>
                        <input type="number" min={1} step="0.1" value={heightUnits} onChange={(e) => setHeightUnits(e.target.value)} />
                    </label>
                </div>

                <div className="custom-widget-builder__data">
                    <div className="widget-settings-drawer__field"><span>Data</span></div>

                    {category === 'chart' && chartFamilySub === 'xy' && (
                        <>
                            <label className="widget-settings-drawer__field">
                                <span>Categories (x-axis, comma-separated)</span>
                                <input type="text" value={categoriesText} onChange={(e) => setCategoriesText(e.target.value)} />
                            </label>
                            {seriesRows.map((row) => (
                                <div className="custom-widget-builder__series-row" key={row.key}>
                                    <input
                                        type="text"
                                        placeholder="Series name"
                                        value={row.name}
                                        onChange={(e) => updateSeriesRow(row.key, { name: e.target.value })}
                                    />
                                    {isCombo && (
                                        <select value={row.type} onChange={(e) => updateSeriesRow(row.key, { type: e.target.value })}>
                                            <option value="column">Bar</option>
                                            <option value="line">Line</option>
                                        </select>
                                    )}
                                    <input
                                        type="text"
                                        placeholder="Values, comma-separated"
                                        value={row.values}
                                        onChange={(e) => updateSeriesRow(row.key, { values: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        className="widget-style-fields__row-remove"
                                        onClick={() => removeSeriesRow(row.key)}
                                        title="Remove series"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="widget-style-fields__add-row" onClick={addSeriesRow}>
                                <i className="fas fa-plus"></i> Add series
                            </button>
                        </>
                    )}

                    {category === 'chart' && chartFamilySub === 'labeled' && (
                        <>
                            {pairRows.map((row) => (
                                <div className="custom-widget-builder__series-row" key={row.key}>
                                    <input
                                        type="text"
                                        placeholder="Label"
                                        value={row.label}
                                        onChange={(e) => updatePairRow(row.key, { label: e.target.value })}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Value"
                                        value={row.value}
                                        onChange={(e) => updatePairRow(row.key, { value: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        className="widget-style-fields__row-remove"
                                        onClick={() => removePairRow(row.key)}
                                        title="Remove"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="widget-style-fields__add-row" onClick={addPairRow}>
                                <i className="fas fa-plus"></i> Add slice
                            </button>
                        </>
                    )}

                    {category === 'chart' && chartFamilySub && chartFamilySub !== 'xy' && chartFamilySub !== 'labeled' && (
                        <div className="widget-style-fields__hint">
                            This style previews with sample data — custom data entry for {variantMeta?.label} is coming soon.
                        </div>
                    )}

                    {category === 'gauge' && (
                        <div className="widget-settings-drawer__row">
                            <label className="widget-settings-drawer__field">
                                <span>Max</span>
                                <input type="number" value={gaugeMax} onChange={(e) => setGaugeMax(e.target.value)} />
                            </label>
                            <label className="widget-settings-drawer__field">
                                <span>Unit</span>
                                <input type="text" value={gaugeUnit} onChange={(e) => setGaugeUnit(e.target.value)} />
                            </label>
                        </div>
                    )}

                    {category === 'gauge' && (
                        <div className="widget-style-fields__hint">
                            The live value is read from the API by matching the widget's title to a meter reading — rename it (e.g. "Voltage R") to connect it.
                        </div>
                    )}

                    {category === 'metric' && !isCustomStyle && (
                        <div className="widget-settings-drawer__row">
                            <label className="widget-settings-drawer__field">
                                <span>Unit</span>
                                <input type="text" value={metricUnit} onChange={(e) => setMetricUnit(e.target.value)} />
                            </label>
                            <label className="widget-settings-drawer__field">
                                <span>Decimal places</span>
                                <input
                                    type="number"
                                    min={0}
                                    max={4}
                                    value={metricPrecision}
                                    onChange={(e) => setMetricPrecision(e.target.value)}
                                />
                            </label>
                        </div>
                    )}

                    {category === 'metric' && !isCustomStyle && (
                        <div className="widget-style-fields__hint">
                            The live value is read from the API by matching the widget's title to a meter reading — rename it (e.g. "Voltage R") to connect it.
                        </div>
                    )}

                    {category === 'metric' && isCustomStyle && (
                        <MetricFieldsEditor
                            fields={customSettings.customFields}
                            onChange={(fields) => patchCustomSettings({ customFields: fields })}
                        />
                    )}

                    {category === 'table' && (
                        <>
                            <label className="widget-settings-drawer__field">
                                <span>Columns (comma-separated)</span>
                                <input type="text" value={tableColumns} onChange={(e) => setTableColumns(e.target.value)} />
                            </label>
                            {tableRows.map((row) => (
                                <div className="custom-widget-builder__series-row" key={row.key}>
                                    <input
                                        type="text"
                                        placeholder="Row values, comma-separated"
                                        value={row.cells}
                                        onChange={(e) => updateTableRow(row.key, e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="widget-style-fields__row-remove"
                                        onClick={() => removeTableRow(row.key)}
                                        title="Remove row"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="widget-style-fields__add-row" onClick={addTableRow}>
                                <i className="fas fa-plus"></i> Add row
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="widget-settings-drawer__footer">
                <button type="button" className="widget-settings-drawer__cancel" onClick={onClose}>Cancel</button>
                <button type="button" className="widget-settings-drawer__save" onClick={handleCreate}>Create Widget</button>
            </div>
        </aside>,
        document.body,
    );
};

export default CustomWidgetBuilder;
